import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { specials } from "@/mocks/specials";
import SearchOverlay from "./SearchOverlay";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "关于", to: "/about" },
];

export default function SiteHeader() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (prefix: string) =>
    prefix === "/" ? location.pathname === "/" : location.pathname.startsWith(prefix);

  const linkClass = (active: boolean) =>
    `rounded-md px-3 py-2 text-sm transition-colors whitespace-nowrap ${
      active
        ? "bg-primary-100 font-semibold text-primary-700"
        : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
    }`;

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/85 backdrop-blur-md">
        <div className="flex h-16 items-center gap-6 px-4 md:px-8">
          <Link to="/" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-lg text-background-50">
              <i className="ri-heart-3-fill" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-extrabold tracking-tight text-foreground-950">
                糖心Vlog
              </span>
              <span className="text-[10px] font-medium tracking-widest text-primary-600">
                TANGXIN VLOG
              </span>
            </span>
          </Link>

          {/* desktop nav */}
          <nav className="ml-2 hidden items-center gap-1 md:flex">
            <NavLink to="/" className={linkClass(isActive("/"))}>
              首页
            </NavLink>

            <div
              className="relative"
              onMouseEnter={() => setOpenMenu("category")}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <button
                type="button"
                onClick={() => setOpenMenu(openMenu === "category" ? null : "category")}
                className={`flex cursor-pointer items-center gap-1 ${linkClass(isActive("/category"))}`}
              >
                分类 <i className="ri-arrow-down-s-line text-base" />
              </button>
              {openMenu === "category" && (
                <div className="absolute left-0 top-full w-60 animate-popIn pt-2">
                  <div className="rounded-lg border border-background-200 bg-background-50 p-2">
                    {categories.map((c) => (
                      <Link
                        key={c.id}
                        to={`/category/${c.id}`}
                        onClick={() => setOpenMenu(null)}
                        className="flex items-center gap-3 rounded-md p-2.5 transition-colors hover:bg-background-100"
                      >
                        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                          <i className={`${c.icon} text-lg`} />
                        </span>
                        <span className="flex flex-col">
                          <span className="text-sm font-medium text-foreground-950">{c.name}</span>
                          <span className="text-xs text-foreground-500">{c.accentLabel}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div
              className="relative"
              onMouseEnter={() => setOpenMenu("special")}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <button
                type="button"
                onClick={() => setOpenMenu(openMenu === "special" ? null : "special")}
                className={`flex cursor-pointer items-center gap-1 ${linkClass(isActive("/special"))}`}
              >
                专题 <i className="ri-arrow-down-s-line text-base" />
              </button>
              {openMenu === "special" && (
                <div className="absolute left-0 top-full w-64 animate-popIn pt-2">
                  <div className="rounded-lg border border-background-200 bg-background-50 p-2">
                    {specials.map((s) => (
                      <Link
                        key={s.id}
                        to={`/special/${s.id}`}
                        onClick={() => setOpenMenu(null)}
                        className="flex items-center gap-3 rounded-md p-2.5 transition-colors hover:bg-background-100"
                      >
                        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-600">
                          <i className="ri-bookmark-3-line text-lg" />
                        </span>
                        <span className="flex flex-col">
                          <span className="text-sm font-medium text-foreground-950">{s.title}</span>
                          <span className="text-xs text-foreground-500">
                            {s.videoIds.length} 个视频
                          </span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {NAV_ITEMS.slice(1).map((item) => (
              <NavLink key={item.to} to={item.to} className={linkClass(isActive(item.to))}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="hidden cursor-pointer items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-500 transition-colors hover:border-primary-300 hover:text-foreground-900 md:flex"
            >
              <i className="ri-search-line" /> 搜索
            </button>
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100"
              aria-label="搜索"
            >
              <i className="ri-search-line text-xl" />
            </button>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
              aria-label="菜单"
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} />
            </button>
          </div>
        </div>

        {/* mobile panel */}
        {mobileOpen && (
          <div className="animate-fadeIn border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                首页
              </Link>
              <p className="px-3 pt-3 pb-1 text-xs font-medium uppercase tracking-wide text-foreground-500">
                视频分类
              </p>
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.id}`}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
                >
                  <i className={`${c.icon} text-primary-500`} /> {c.name}
                </Link>
              ))}
              <p className="px-3 pt-3 pb-1 text-xs font-medium uppercase tracking-wide text-foreground-500">
                精选专题
              </p>
              {specials.map((s) => (
                <Link
                  key={s.id}
                  to={`/special/${s.id}`}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
                >
                  <i className="ri-bookmark-3-line text-accent-500" /> {s.title}
                </Link>
              ))}
              <Link
                to="/about"
                onClick={() => setMobileOpen(false)}
                className="mt-2 rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                关于与联系
              </Link>
              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false);
                  navigate("/search");
                }}
                className="mt-1 flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50"
              >
                <i className="ri-search-line" /> 搜索视频
              </button>
            </div>
          </div>
        )}
      </header>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}